import Column from "@/Components/Grid/Column";
import Container from "@/Components/Grid/Container";
import Row from "@/Components/Grid/Row";
import { PageProps } from "@/types";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Head, Link, router } from "@inertiajs/react";
import { Fragment, useEffect, useRef, useState } from "react";

import HeadingWithDecor from "@/Components/HeadingWithDecor";
import PatientPodcastBlock from "@/Components/Patient/PatientPodcastBlock";
import VideoPlayer from "@/Components/VideoPlayer";
import { formatWatchTime } from "@/Helpers/formatWatchTime";
import useMediaQuery from "@/Hooks/useMediaQuery";
import PatientLayout from "@/Layouts/PatientLayout";
import { ProgressEvent } from "@/types/ProgressEvent";
import { PodcastModel } from "@/types/models/PodcastModel";
import { PodcastSeriesModel } from "@/types/models/PodcastSeriesModel";
import { Transition } from "@headlessui/react";
import axios from "axios";
import ReactPlayer from "react-player";
import BlockedVideoPlayer from "@/Components/BlockedVideoPlayer";

const PodcastSingle = ({ auth, podcast, podcastSeries, similarPodcasts, currentPodcastIndex }: PageProps<{
    podcast: PodcastModel,
    podcastSeries: PodcastSeriesModel,
    similarPodcasts: PodcastSeriesModel[],
    currentPodcastIndex: number,
}>) => {
    const isMobile = useMediaQuery();

    const [hoverIndex, setHoverIndex] = useState<number>(podcast.rate ? podcast.rate : -1);
    const [currentRate, setCurrentRate] = useState<number>(podcast.rate ? podcast.rate : -1);
    const [showDesc, setShowDesc] = useState<boolean>(false);
    const [playerReady, setPlayerReady] = useState(false);
    const [lastReportedTime, setLastReportedTime] = useState(0);
    const videoPlayerRef = useRef<ReactPlayer>(null);

    useEffect(() => {
        if (playerReady && videoPlayerRef.current && podcast.history_time) {
            videoPlayerRef.current.seekTo(podcast.history_time);
        }
    }, [playerReady, podcast.history_time]);

    useEffect(() => {
        if (lastReportedTime > 0) {
            axios.post(route('app.storeMaterialProgress'), {
                time: lastReportedTime,
                modelId: podcast.id,
                modelType: 'Podcast'
            })
        }
    }, [lastReportedTime])

    const handleProgress = (progress: ProgressEvent) => {
        if (progress.playedSeconds > 2 && progress.playedSeconds < 3) {
            setLastReportedTime(Math.floor(progress.playedSeconds));
        }

        if (Math.floor(progress.playedSeconds) % 10 === 0 && Math.floor(progress.playedSeconds) !== lastReportedTime) {
            setLastReportedTime(Math.floor(progress.playedSeconds));
        }
    }

    const handlePlayerReady = () => {
        setPlayerReady(true);
    };

    const splittedDescription = (description: string, amountOfWords: number) => {
        const wordsArray = description.split(' ');

        const firstPart = wordsArray.slice(0, amountOfWords);
        const secondPart = wordsArray.slice(amountOfWords);

        return [firstPart, secondPart];
    }

    const formatDate = (dateString: string) => {
        return dateString.split(' ')[0];
    };

    const handleMaterialRate = (rate: number) => {
        router.post(route('app.rateMaterial'), {
            rate: rate + 1,
            modelId: podcast.id,
            modelType: 'Podcast'
        }, {
            preserveScroll: true,
            preserveState: false,
            onSuccess: () => {
                setCurrentRate(rate);
            }
        })
    }

    const resolveExtension = (file: string): string => {
        return file.replace(/(\/images\/[^.]+)\.[a-z]+/i, "$1.webp");
    }

    return (
        <PatientLayout>
            <Head title="Posłuchaj nagrania" />
            <Container className="mt-8">
                <Row>
                    <Column>
                        <Link href={'/dla-pacjenta'} className="outline-btn inline-flex items-center gap-x-2 justify-end border-1 border-accordorange mb-10">
                            Powrót
                            <Icon icon={`tabler:arrow-back`} width={24} className="text-arcticMist" />
                        </Link>
                    </Column>
                </Row>
                <Row className="mb-14">
                    <Column xl={5} className="maxMd:!px-0">
                        <div className="w-full aspect-square relative">
                            <img src={'/patient' + podcast.hero + '/' + podcastSeries.id + '/podcastSeries'} alt="Okładka" className="absolute inset-0 z-0 w-full h-full object-cover object-center blur-md" />
                            <div className="absolute inset-0 bg-midnightAbyss/80 blur-md z-10"></div>
                            <div className="w-full relative z-10 pt-8 pb-6 px-8 flex flex-col items-center h-full maxMd:p-0">
                                <div className="max-w-[55%] w-full aspect-square mb-8 mt-auto">
                                    <img src={'/patient' + podcast.hero + '/' + podcastSeries.id + '/podcastSeries'} alt="Okładka" className="w-full h-full object-cover object-center rounded-lg" />
                                </div>

                                <div className="mt-auto w-full">
                                    <BlockedVideoPlayer
                                        controls={true}
                                        url={'/patient' + podcast.audio + '/' + podcastSeries.id + '/podcastSeries'}
                                        className='mt-auto w-full'
                                        width={`100%`}
                                        height={`48px`}
                                        onProgress={handleProgress}
                                        onReady={handlePlayerReady}
                                        ref={videoPlayerRef}
                                    />
                                </div>
                            </div>
                        </div>
                    </Column>
                    <Column xl={7}>
                        <div className="pl-8 maxMd:pl-0 maxMd:mt-12">
                            <p className="text-arcticMist text-14 tracking-[0.21px]">{podcastSeries.name}</p>
                            <h1 className="text-arcticMist h3 font-semibold tracking-[0.64px] mb-4">{podcast.name}</h1>
                            <div className="flex gap-x-4 mb-8 maxMd:flex-wrap">
                                <p className="text-arcticMist text-body tracking-[0.27px] maxMd:w-full">Prowadzący:</p>
                                <div>
                                    {podcast.authors && podcast.authors.map((author, i) => (
                                        <p key={author.id} className="text-accordorange text-body tracking-[0.27px]">{`${author.degree} ${author.name_surname}`}</p>
                                    ))}
                                </div>
                            </div>
                            <div className="info mb-8">
                                <hr className="border-t-1 border-royalPeriwinkle" />
                                <div className="py-4">
                                    <div className="flex maxMd:flex-wrap maxMd:gap-y-8">
                                        <div className="col pl-4 pr-8 maxMd:w-1/2 maxMd:px-4 relative">
                                            <p className="text-arcticMist text-base tracking-[0.24px] font-semibold mb-2">Czas słuchania</p>
                                            <div className="flex items-center gap-x-2">
                                                <Icon icon={`fa6-solid:headphones`} width={24} className="text-arcticMist" />
                                                <p className="text-arcticMist text-base tracking-[0.24px]">{formatWatchTime(podcast.time)}</p>
                                            </div>
                                            <div className="absolute right-2 top-1/2 -translate-y-1/2 md:hidden block">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="2" height="56" viewBox="0 0 2 56" fill="none">
                                                    <path d="M1.00001 0L1 56" stroke="#393ABF" strokeDasharray="8 8" />
                                                </svg>
                                            </div>
                                        </div>

                                        <div className="col px-8 maxMd:w-1/2 maxMd:px-4">
                                            <p className="text-arcticMist text-base tracking-[0.24px] font-semibold mb-2">Odcinki</p>
                                            <p className="text-arcticMist text-base tracking-[0.24px]"><span className="text-accordorange font-semibold">{currentPodcastIndex} </span>z {podcastSeries.podcasts.length}</p>
                                        </div>
                                        <div className="col px-8 maxMd:w-1/2 maxMd:px-4 relative">
                                            <p className="text-arcticMist text-base tracking-[0.24px] font-semibold mb-2">Kategoria</p>
                                            {podcastSeries.therapeutic_areas && podcastSeries.therapeutic_areas.map((area, i) => (
                                                <p key={i} className="text-arcticMist text-base tracking-[0.24px]">{area.name}</p>
                                            ))}
                                            <div className="absolute right-2 top-0 md:hidden block top-1/2 -translate-y-1/2">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="2" height="56" viewBox="0 0 2 56" fill="none">
                                                    <path d="M1.00001 0L1 56" stroke="#393ABF" strokeDasharray="8 8" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <hr className="border-b-1 border-royalPeriwinkle" />
                            </div>
                            <Transition
                                show={!showDesc}
                                as={`div`}
                                enter="transition-theme"
                                enterFrom="opacity-0"
                                enterTo="opacity-100"
                                leave="transition-theme"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                            >
                                <div className="desc text-arcticMist text-body mb-6">
                                    <p>{splittedDescription(podcast.description, 10)[0].join(' ')}...</p>
                                </div>
                                <button type="button" onClick={() => setShowDesc((prev) => !prev)} className="cursor-pointer flex text-arcticMist text-14 leading-18 items-center gap-x-2 mb-6">
                                    Pokaż więcej
                                    <Icon icon={`tabler:chevron-down`} className="text-accordorange" width={24} />
                                </button>
                            </Transition>
                            <Transition
                                show={showDesc}
                                as={`div`}
                                enter="transition-theme"
                                enterFrom="opacity-0"
                                enterTo="opacity-100"
                                leave="transition-theme"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                            >
                                <div className="text-arcticMist text-body">
                                    {/* <p>{splittedDescription(podcast.description, 10)[1].join(' ')}</p> */}
                                    <p>{podcast.description}</p>
                                    <p className="font-semibold mt-6 mb-2">Data publikacji</p>
                                    <p className="text-14 leading-18">{formatDate(podcast.created_at)}</p>
                                </div>
                                <button type="button" onClick={() => setShowDesc((prev) => !prev)} className="mt-3 cursor-pointer flex text-arcticMist text-14 leading-18 items-center gap-x-2">
                                    Pokaż mniej
                                    <Icon icon={`tabler:chevron-down`} className="text-accordorange rotate-180" width={24} />
                                </button>
                            </Transition>
                        </div>
                    </Column>
                </Row>
                {!isMobile &&
                    <>
                        <Row>
                            <Column>
                                <HeadingWithDecor>
                                    Więcej odcinków
                                </HeadingWithDecor>
                            </Column>
                            <Column>
                                <Row className="mb-8">
                                    {podcastSeries.podcasts && podcastSeries.podcasts.map((p, i) => {
                                        return (
                                            <Column xl={2} md={4} lg={3} className="mb-6" key={i}>
                                                <Link href={route('front.patient.podcasts.single', { podcastSeries: podcastSeries, podcast: p.slug })}>
                                                    <img src={'/patient' + p.hero + '/' + podcastSeries.id + '/podcastSeries'} className="object-cover rounded-2xl aspect-square h-auto w-full" />
                                                    <p className="text-arcticMist text-body tracking-[0.27px] font-semibold">{p.name}</p>
                                                </Link>
                                            </Column>
                                        )
                                    })}
                                </Row>
                            </Column>
                        </Row>
                        {similarPodcasts.length > 0 &&
                            <Row>
                                <Column>
                                    <HeadingWithDecor>
                                        Podcasty o podobnej tematyce
                                    </HeadingWithDecor>
                                </Column>
                                <Column>
                                    <Row className="mb-17">
                                        {similarPodcasts && similarPodcasts.map((sm, i) => {
                                            if (sm.podcasts[0]) {
                                                return (
                                                    <Column xl={2} md={4} lg={3} className="mb-6" key={i}>
                                                        <PatientPodcastBlock
                                                            image={'patient' + sm.podcasts[0].hero + '/' + sm.id + '/podcastSeries'}
                                                            createdAt={formatDate(sm.created_at)}
                                                            title={sm.name}
                                                            link={route('front.patient.podcasts.single', { podcastSeries: sm, podcast: sm.podcasts[0]?.slug })}
                                                        />
                                                    </Column>
                                                )
                                            }
                                        })}
                                    </Row>
                                </Column>
                            </Row>
                        }
                    </>
                }
                {isMobile &&
                    <Row>
                        <Column>
                            <HeadingWithDecor>
                                Więcej odcinków
                            </HeadingWithDecor>
                        </Column>
                        {podcastSeries.podcasts && podcastSeries.podcasts.map((p, i) => {
                            return (
                                <Column key={p.id}>
                                    <div className="flex gap-x-4 py-6 items-center">
                                        <Link href={route('front.patient.podcasts.single', { podcastSeries: podcastSeries, podcast: p.slug })}>
                                            <img src={'/patient' + p.hero + '/' + podcastSeries.id + '/podcastSeries'} className="object-cover rounded-2xl aspect-square h-auto w-full" />
                                            <p className="text-arcticMist text-body tracking-[0.27px] font-semibold">{p.name}</p>
                                        </Link>
                                    </div>
                                    <div className="border border-royalPeriwinkle"></div>
                                </Column>
                            )
                        })}
                        {similarPodcasts.length > 0 &&
                            <Column className="mt-8">
                                <HeadingWithDecor>
                                    Podcasty o podobnej tematyce
                                </HeadingWithDecor>
                                {similarPodcasts && similarPodcasts.map((sm, i) => {
                                    if (sm.podcasts[0]) {
                                        return (
                                            <Fragment key={sm.id}>
                                                <PatientPodcastBlock
                                                    image={'patient' + sm.podcasts[0].hero + '/' + sm.id + '/podcastSeries'}
                                                    createdAt={formatDate(sm.created_at)}
                                                    title={sm.name}
                                                    link={route('front.patient.podcasts.single', { podcastSeries: sm, podcast: sm.podcasts[0]?.slug })}
                                                />
                                                <div className="border border-royalPeriwinkle"></div>
                                            </Fragment>
                                        )
                                    }
                                })}
                            </Column>
                        }
                    </Row>
                }
            </Container>
        </PatientLayout>
    )
}

export default PodcastSingle;